import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { RichText } from '@components/common/RichText';
import './MultipleChoiceQuestion.css';

/**
 * Multiple Choice Question Component
 */
export function MultipleChoiceQuestion({ 
  question, 
  value, 
  onChange, 
  disabled = false, 
  showCorrect = false,
  incorrectAnswers = [],
  disabledOptions = [] // Options disabled by 50/50 lifeline
}) {
  const shuffledOptions = useMemo(() => {
    if (!question?.options) return [];
    const copy = [...question.options];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, [question?.id, question?.options]);
  const handleOptionClick = (option) => {
    if (disabled) return;
    
    const isCorrect = option === question.correctAnswer;
    const wasIncorrectChoice = incorrectAnswers.includes(option);
    const isFiftyFiftyDisabled = disabledOptions.includes(option);
    
    // Don't allow selecting 50/50 disabled options or previously incorrect options
    if (isFiftyFiftyDisabled) return;
    
    // Only allow selecting options that are correct or haven't been tried yet
    if (!wasIncorrectChoice || isCorrect) {
      onChange(option);
    }
  };

  return (
    <div className="multiple-choice-question">
      {shuffledOptions.map((option, index) => {
        const isSelected = value === option;
        const isCorrect = option === question.correctAnswer;
        const wasIncorrectChoice = incorrectAnswers.includes(option);
        const isFiftyFiftyDisabled = disabledOptions.includes(option);
        const isOptionDisabled = wasIncorrectChoice || isFiftyFiftyDisabled; // Disable previously incorrect or 50/50 disabled answers
        
        let variantClass = '';
        
        // Show correct answer when revealed
        if (showCorrect && isCorrect) {
          variantClass = 'option-correct';
        }
        // Highlight all incorrect answers (current and previous)
        else if (isSelected && !isCorrect) {
          variantClass = 'option-incorrect';
        }
        else if (wasIncorrectChoice) {
          variantClass = 'option-incorrect';
        }
        // Show 50/50 disabled options with special styling
        else if (isFiftyFiftyDisabled) {
          variantClass = 'option-fifty-fifty-disabled';
        }

        return (
          <div
            key={index}
            className={`option-card ${isSelected ? 'selected' : ''} ${variantClass} ${isOptionDisabled ? 'disabled' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => !isOptionDisabled && handleOptionClick(option)}
            role="button"
            tabIndex={isOptionDisabled ? -1 : (disabled ? -1 : 0)}
            onKeyPress={(e) => {
              if (!isOptionDisabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleOptionClick(option);
              }
            }}
          >
            <Form.Check
              type="radio"
              id={`option-${question.id}-${index}`}
              name={`question-${question.id}`}
              checked={isSelected}
              onChange={() => {}} // Handled by parent div click
              disabled={disabled || isOptionDisabled}
              className="option-radio"
              tabIndex={-1}
            />
            <label 
              htmlFor={`option-${question.id}-${index}`}
              className="option-label"
              onClick={(e) => e.preventDefault()} // Prevent double-trigger
            >
              <RichText value={option} />
            </label>
          </div>
        );
      })}
    </div>
  );
}
